import { useEffect, useMemo, useRef, useState } from 'react';
import { Ban, Timer, SkipForward, Check, Play } from 'lucide-react';

// Taboo — turn-based word explanation game with forbidden words.
// Same turn flow as Alias:
//  - Learner explains first (60s), then teacher (60s), then alternate.
//  - Only the current explainer sees the card: the target word plus the
//    forbidden words listed under it.
//  - Explainer taps Skip / Taboo! (said a forbidden word) / Guessed.
//  - Card pool is shared across rounds; each new round continues where the
//    previous left off.
//  - Between rounds: review screen. Teacher can end the game there.
//
// Controlled game surface: renders from `gameState` and reports every
// transition through `onGameStateChange`. In preview mode it keeps local
// state so it plays on a single device without any role split.

const TURN_DURATION_SEC = 60;

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export const normalizeTabooCards = (list) => (Array.isArray(list) ? list : [])
  .map((c) => ({
    word: typeof c?.word === 'string' ? c.word.trim() : '',
    forbidden: Array.isArray(c?.forbidden)
      ? c.forbidden.filter((f) => typeof f === 'string' && f.trim().length > 0).map((f) => f.trim())
      : [],
  }))
  .filter((c) => c.word.length > 0);

export const createInitialGameState = () => ({
  phase: 'idle',
  cards: [],
  index: 0,
  rounds: [], // completed rounds: [{ explainer, guessed: [], skipped: [], taboo: [] }]
  currentRound: null,
  roundStartedAt: null,
});

const emptyRound = (explainer) => ({ explainer, guessed: [], skipped: [], taboo: [] });

const startGame = (state, cards) => ({
  ...state,
  phase: 'round_active',
  cards: shuffle(cards),
  index: 0,
  rounds: [],
  currentRound: emptyRound('student'),
  roundStartedAt: Date.now(),
});

const isActivePhase = (state) => state.phase === 'round_active' || state.phase === 'round_grace';
const isGracePhase = (state) => state.phase === 'round_grace';

const nextExplainerFor = (state) => {
  const last = state.rounds[state.rounds.length - 1];
  return last?.explainer === 'student' ? 'teacher' : 'student';
};

// kind: 'guessed' | 'skipped' | 'taboo'. Only the target word is stored in
// round buckets — the forbidden list isn't needed for review.
const advanceCard = (state, kind) => {
  if (!state.currentRound) return state;
  const card = state.cards[state.index];
  if (!card) return state;
  const updatedRound = {
    ...state.currentRound,
    [kind]: [...(state.currentRound[kind] || []), card.word],
  };
  const nextIndex = state.index + 1;
  const outOfCards = nextIndex >= state.cards.length;
  if (isGracePhase(state) || outOfCards) {
    return {
      ...state,
      index: nextIndex,
      rounds: [...state.rounds, updatedRound],
      currentRound: null,
      phase: 'round_review',
      roundStartedAt: null,
    };
  }
  return { ...state, currentRound: updatedRound, index: nextIndex };
};

const endTurnByTimer = (state) => {
  if (state.phase !== 'round_active') return state;
  if (state.index >= state.cards.length) {
    return {
      ...state,
      rounds: state.currentRound ? [...state.rounds, state.currentRound] : state.rounds,
      currentRound: null,
      phase: 'round_review',
      roundStartedAt: null,
    };
  }
  return { ...state, phase: 'round_grace', roundStartedAt: null };
};

const startNextRound = (state) => ({
  ...state,
  phase: 'round_active',
  currentRound: emptyRound(nextExplainerFor(state)),
  roundStartedAt: Date.now(),
});

const showFinal = (state) => ({ ...state, phase: 'final', currentRound: null, roundStartedAt: null });

// -------- Timer hook --------

const useTimeLeft = (state) => {
  const [now, setNow] = useState(() => Date.now());
  const isActive = state.phase === 'round_active';
  useEffect(() => {
    if (!isActive) return undefined;
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, [isActive]);
  if (!isActive || !state.roundStartedAt) return TURN_DURATION_SEC;
  return Math.max(0, TURN_DURATION_SEC - Math.floor((now - state.roundStartedAt) / 1000));
};

// -------- Static preview (editor canvas) --------

const StaticPreview = ({ cards }) => {
  const sample = cards[0];
  return (
    <div className="w-full h-full flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-brand-primary/40 bg-brand-sage/10 p-4 text-brand-primary">
      <Ban size={40} className="mb-2 opacity-70" />
      <div className="text-lg font-semibold">Taboo</div>
      <div className="text-xs opacity-70 mt-1">
        {cards.length > 0 ? `${cards.length} card${cards.length === 1 ? '' : 's'}` : 'No cards yet'}
      </div>
      {sample && (
        <div className="mt-3 text-center">
          <div className="text-sm font-bold text-gray-800">{sample.word}</div>
          {sample.forbidden.length > 0 && (
            <div className="text-[11px] text-red-600/80 mt-0.5">{sample.forbidden.join(' · ')}</div>
          )}
        </div>
      )}
    </div>
  );
};

// -------- UI atoms --------

const Timer60 = ({ seconds }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-primary text-white text-sm font-semibold">
    <Timer size={14} />
    <span>{String(seconds).padStart(2, '0')}s</span>
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, icon: Icon }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2A4D25] text-white text-sm font-semibold hover:bg-[#1e3a1a] transition disabled:opacity-40 disabled:cursor-not-allowed"
  >
    {Icon && <Icon size={16} />}
    <span>{children}</span>
  </button>
);

const ExplainerCard = ({ card, timeLeft, onGuessed, onSkip, onTaboo, cardsLeft }) => (
  <div className="w-full h-full flex flex-col items-center justify-between p-4 gap-3">
    <div className="w-full flex items-center justify-between text-xs text-gray-600">
      <span>Cards left: {cardsLeft}</span>
      {timeLeft != null ? <Timer60 seconds={timeLeft} /> : <span />}
    </div>
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center w-full gap-3 overflow-y-auto">
      <div className="text-4xl md:text-5xl font-bold text-gray-900 text-center break-words px-4">
        {card?.word || '—'}
      </div>
      {card?.forbidden?.length > 0 && (
        <div className="w-full max-w-xs rounded-xl border border-red-200 bg-red-50 px-4 py-2.5">
          <div className="flex items-center justify-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-red-600 mb-1.5">
            <Ban size={12} /> Don't say
          </div>
          <div className="flex flex-col items-center gap-0.5">
            {card.forbidden.map((f, i) => (
              <div key={`${f}-${i}`} className="text-base font-semibold text-red-700 text-center break-words">
                {f}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
    <div className="flex flex-wrap gap-2 w-full justify-center">
      <button
        onClick={onSkip}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition"
      >
        <SkipForward size={16} /> Skip
      </button>
      <button
        onClick={onTaboo}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-white border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 transition"
      >
        <Ban size={16} /> Taboo!
      </button>
      <button
        onClick={onGuessed}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#2A4D25] text-white text-sm font-semibold hover:bg-[#1e3a1a] transition"
      >
        <Check size={16} /> Guessed
      </button>
    </div>
  </div>
);

const GuesserView = ({ timeLeft, score, cardsLeft, waitingLabel }) => (
  <div className="w-full h-full flex flex-col items-center justify-center p-4 gap-4">
    {timeLeft != null && <Timer60 seconds={timeLeft} />}
    <div className="text-sm text-gray-500">{waitingLabel}</div>
    <div className="grid grid-cols-2 gap-6 text-center">
      <div>
        <div className="text-3xl font-bold text-brand-primary">{score}</div>
        <div className="text-xs text-gray-500">Guessed</div>
      </div>
      <div>
        <div className="text-3xl font-bold text-gray-700">{cardsLeft}</div>
        <div className="text-xs text-gray-500">Cards left</div>
      </div>
    </div>
  </div>
);

const WORD_TONES = {
  good: { title: 'text-emerald-700', chip: 'bg-emerald-100 text-emerald-800' },
  bad: { title: 'text-gray-500', chip: 'bg-gray-100 text-gray-600 line-through' },
  taboo: { title: 'text-red-600', chip: 'bg-red-50 text-red-700 line-through' },
};

const WordList = ({ title, words = [], tone }) => (
  <div className="min-w-0">
    <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${WORD_TONES[tone].title}`}>
      {title} ({words.length})
    </div>
    <div className="flex flex-wrap gap-1.5">
      {words.length === 0 ? (
        <span className="text-xs text-gray-400 italic">none</span>
      ) : (
        words.map((w, i) => (
          <span key={`${w}-${i}`} className={`px-2 py-0.5 rounded text-xs ${WORD_TONES[tone].chip}`}>
            {w}
          </span>
        ))
      )}
    </div>
  </div>
);

const RoundLists = ({ round }) => (
  <>
    <WordList title="Guessed" words={round.guessed} tone="good" />
    <WordList title="Taboo" words={round.taboo} tone="taboo" />
    <WordList title="Skipped" words={round.skipped} tone="bad" />
  </>
);

const ReviewScreen = ({ roundStats, ctaLabel, onCta, waitLabel, onEndGame }) => (
  <div className="w-full h-full flex flex-col p-4 gap-3 overflow-hidden">
    <div className="text-center">
      <div className="text-2xl font-bold text-brand-primary">
        {roundStats.guessed.length} guessed
      </div>
    </div>
    <div className="flex-1 overflow-y-auto space-y-3 pr-1">
      <RoundLists round={roundStats} />
    </div>
    <div className="flex flex-wrap justify-center gap-2 pt-2">
      {onCta ? (
        <PrimaryButton onClick={onCta} icon={Play}>{ctaLabel}</PrimaryButton>
      ) : waitLabel ? (
        <div className="text-sm text-gray-500 italic">{waitLabel}</div>
      ) : null}
      {onEndGame && (
        <button
          onClick={onEndGame}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition"
        >
          Завершити гру
        </button>
      )}
    </div>
  </div>
);

const sumByExplainer = (rounds, explainer, bucket) =>
  rounds
    .filter((r) => r.explainer === explainer)
    .reduce((acc, r) => acc + (r[bucket]?.length || 0), 0);

const FinalScreen = ({ state, userRole, previewMode }) => {
  const rounds = state.rounds;
  const studentExplained = sumByExplainer(rounds, 'student', 'guessed');
  const teacherExplained = sumByExplainer(rounds, 'teacher', 'guessed');

  const roundList = (
    <div className="flex-1 overflow-y-auto space-y-3 pr-1">
      {rounds.map((r, i) => (
        <div key={i} className="space-y-1">
          <div className="text-xs font-semibold text-gray-500 uppercase pt-1">
            Round {i + 1} — {r.explainer === 'student' ? 'learner' : 'teacher'} explained
          </div>
          <RoundLists round={r} />
        </div>
      ))}
    </div>
  );

  const showMirrored = !previewMode && userRole;
  const left = showMirrored
    ? { value: userRole === 'student' ? teacherExplained : studentExplained, label: 'You guessed' }
    : { value: studentExplained, label: 'Learner explained' };
  const right = showMirrored
    ? { value: userRole === 'student' ? studentExplained : teacherExplained, label: 'You explained' }
    : { value: teacherExplained, label: 'Teacher explained' };

  return (
    <div className="w-full h-full flex flex-col p-4 gap-3 overflow-hidden">
      <div className="text-center text-lg font-bold text-brand-primary">Game over</div>
      <div className="grid grid-cols-2 gap-4 text-center">
        {[left, right].map((s) => (
          <div key={s.label}>
            <div className="text-3xl font-bold text-brand-primary">{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>
      {roundList}
    </div>
  );
};

// -------- Core game surface --------

const TabooGameCore = ({ cards, state, onChange, userRole, previewMode }) => {
  const timeLeft = useTimeLeft(state);
  const endedRef = useRef(false);

  // Auto-end turn when timer hits 0.
  useEffect(() => {
    if (state.phase !== 'round_active') {
      endedRef.current = false;
      return;
    }
    if (timeLeft === 0 && !endedRef.current) {
      endedRef.current = true;
      onChange(endTurnByTimer(state));
    }
  }, [timeLeft, state, onChange]);

  const currentExplainer = state.currentRound?.explainer ?? null;
  const isExplainer = previewMode || (currentExplainer && userRole === currentExplainer);

  const canStartGame = previewMode || userRole === 'student';
  const nextExplainer = state.rounds.length > 0 ? nextExplainerFor(state) : 'student';
  const canStartNextRound = previewMode || userRole === nextExplainer;
  const canEndGame = previewMode || userRole === 'teacher';
  const stateCards = state.cards || [];
  const cardsRemaining = state.index < stateCards.length;
  const cardsLeftCount = Math.max(0, stateCards.length - state.index);

  if (state.phase === 'idle') {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-4 gap-3 text-center">
        <Ban size={32} className="text-brand-primary" />
        <div className="text-lg font-semibold text-gray-800">Taboo</div>
        <div className="text-sm text-gray-500 max-w-xs">
          Explain the word without saying any of the words below it. Learner explains first, then swap. One minute each round.
        </div>
        {canStartGame ? (
          <PrimaryButton
            onClick={() => onChange(startGame(state, cards))}
            disabled={cards.length === 0}
            icon={Play}
          >
            Start
          </PrimaryButton>
        ) : (
          <div className="text-xs text-gray-400 italic">Waiting for learner to start…</div>
        )}
      </div>
    );
  }

  if (isActivePhase(state)) {
    const score = state.currentRound?.guessed.length ?? 0;
    const grace = isGracePhase(state);
    const displayTimeLeft = grace ? null : timeLeft;
    if (isExplainer) {
      return (
        <ExplainerCard
          card={stateCards[state.index]}
          timeLeft={displayTimeLeft}
          onGuessed={() => onChange(advanceCard(state, 'guessed'))}
          onSkip={() => onChange(advanceCard(state, 'skipped'))}
          onTaboo={() => onChange(advanceCard(state, 'taboo'))}
          cardsLeft={cardsLeftCount}
        />
      );
    }
    const explainerLabel = currentExplainer === 'student' ? 'Learner' : 'Teacher';
    return (
      <GuesserView
        timeLeft={displayTimeLeft}
        score={score}
        cardsLeft={cardsLeftCount}
        waitingLabel={grace ? `${explainerLabel} is finishing the last card…` : `${explainerLabel} is explaining`}
      />
    );
  }

  if (state.phase === 'round_review') {
    const lastRound = state.rounds[state.rounds.length - 1];
    if (!lastRound) return null;
    return (
      <ReviewScreen
        roundStats={lastRound}
        ctaLabel={nextExplainer === 'student' ? 'Start learner round' : 'Start teacher round'}
        onCta={cardsRemaining && canStartNextRound ? () => onChange(startNextRound(state)) : null}
        waitLabel={
          cardsRemaining
            ? nextExplainer === 'student' ? 'Waiting for learner…' : 'Waiting for teacher…'
            : ''
        }
        onEndGame={canEndGame ? () => onChange(showFinal(state)) : null}
      />
    );
  }

  return <FinalScreen state={state} userRole={userRole} previewMode={previewMode} />;
};

// -------- Public entry point --------

const TabooElement = ({
  element,
  isEditor = false,
  previewMode = false,
  userRole = null,
  gameState = null,
  onGameStateChange = null,
}) => {
  const cards = useMemo(() => normalizeTabooCards(element?.content?.cards), [element?.content?.cards]);

  if (isEditor) {
    return <StaticPreview cards={cards} />;
  }

  if (previewMode) {
    return <TabooPreviewShell cards={cards} />;
  }

  const state = gameState || createInitialGameState();
  const handleChange = (next) => {
    if (onGameStateChange) onGameStateChange(next);
  };
  return (
    <TabooGameCore
      cards={cards}
      state={state}
      onChange={handleChange}
      userRole={userRole}
      previewMode={false}
    />
  );
};

const TabooPreviewShell = ({ cards }) => {
  const [state, setState] = useState(() => createInitialGameState());
  return (
    <TabooGameCore
      cards={cards}
      state={state}
      onChange={setState}
      userRole={null}
      previewMode
    />
  );
};

export default TabooElement;
